

// MODULES
import { mapGetters, mapActions } from 'vuex';

// SOPHEOS
import { removeWhitespaces } from '@sopheos/orbjs_rules';

// MIXINS
import errorsMixin from '../mixins/error-handler';

// LIBRARIES
import { getDisplayPlaceName, getPlaceNameWithPreposition, getShapeId } from '../libraries/utils';
import reasons from '../libraries/lang/reasons';

// ASSETS
import facilitiesLang from '../assets/lang/facilities.json';

// API
import * as apiFavorite from '../api/private/favorite';
import * as apiReport from '../api/private/report';

// ASYNC COMPONENT
import SLoadingState from './SLoadingState.vue';
import SErrorState from './SErrorState.vue';

const SUploadImageLazy = () => ({
  component: import('./SUploadImage.vue'),
  loading: SLoadingState,
  error: SErrorState,
  timeout: 60000,
});

export default {
  name: 'SDetailItem',
  components: {
    SUploadImageLazy,
  },
  mixins: [errorsMixin],
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    commentsError: {
      type: Boolean,
      default: false,
    },
    commentsErrorMessage: {
      type: String,
      default: '',
    },
    favoriteDisabled: {
      type: Boolean,
      default: false,
    },
    playground: {
      type: Object,
      default: null,
    },
    listComments: {
      type: Array,
      default: () => [],
    },
    listHasComments: {
      type: Array,
      default: () => [],
    },
    listHasContributions: {
      type: Array,
      default: () => [],
    },
    listContributions: {
      type: Array,
      default: () => [],
    },
    listContributors: {
      type: Array,
      default: () => [],
    },
    mobileLayout: {
      type: Boolean,
      default: false,
    },
    sharedError: {
      type: Object,
      default: () => ({}),
    },
    loadingContributions: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fieldsRefs: ['reports-context', 'reports-playground', 'reports-form', 'reports-reason', 'reports-report_uuid', 'reports-message'],
      // TOOLBAR
      appBarObserver: null,
      hoverCarousel: true,
      appBarHidden: true,
      flatToolbar: true,
      hideOnScroll: true,
      scrollBoxReady: false,
      // CONTENT
      detail: null,
      facilitiesData: facilitiesLang.playground,
      networks: [
        {
          name: 'twitter',
          img: '/img/socials/twitter.svg',
        },
        {
          name: 'facebook',
          img: '/img/socials/facebook.svg',
        },
      ],
      // CONTRIBUTION
      ongoingContrib: 0,
      formVisible: {
        street: false,
        facilities: false,
        description: false,
      },
      // CAROUSEL
      waitCarouselReport: false,
      openCarouselReport: false,
      // FAVORITE
      waitFavCall: false,
      favoriteTimer: null,
      loadingFavorite: false,
      // REPORT
      modalReport: false,
      reportReason: null,
      reportUuid: null,
      reportMessage: null,
      listReasons: [],
      loadingReport: false,
      waitCommentReport: false,
      openCommentReport: false,
      waitContributorsReport: false,
      openContributorsReport: false,
      // BANNER
      contributionBanner: false,
      contributionBannerText: null,
      // TOOLTIP
      toggleTooltipContribution: false,
      toggleTooltipFavoriteDisabled: false,
      // DIALOG
      modalLogin: false,
      showFullscreen: false,
      fullscreenButtons: {},
      fullscreenOptions: {},
      fullscreenTitle: '',
      fullscreenLabel: '',
    };
  },
  computed: {
    ...mapGetters({
      getBackArrow: 'transferData/getBackArrow',
      getListFavorites: 'transferData/getListFavorites',
      getUser: 'preferences/getUser',
      getWindowWidth: 'screen/getWindowWidth',
      isAuth: 'authToken/isAuth',
      isWGte: 'screen/isWGte',
      userSecured: 'preferences/userSecured',
      getContext: 'transferData/getContext',
    }),

    // DETAIL -----------------------------------------------------------------------------------------
    playgroundAdminAlias() {
      if (this.playground) {
        return this.playground.admin ? this.playground.admin.alias : this.playground.country.alias;
      }
      return null;
    },
    playgroundPlaceAlias() {
      return this.playground.city.alias;
    },
    playgroundPlaceName() {
      return getDisplayPlaceName(this.playground.city, this.playground.sub);
    },
    placeNameWithPreposition() {
      return getPlaceNameWithPreposition(this.playgroundPlaceName);
    },
    playgroundUrl() {
      return `${process.env.VUE_APP_URL}${this.playgroundAdminAlias}/${this.playgroundPlaceAlias}/${this.playground.id}`;
    },
    getComments() {
      return this.listComments.find((play) => play.id === this.playground.id) ?? [];
    },
    getHasComment() {
      return this.listHasComments.find((play) => play.id === this.playground.id) ?? { id: this.playground.id, hasComment: null };
    },
    getHasContributions() {
      const contrib = this.listHasContributions.find((play) => play.id === this.playground.id);
      if (contrib) {
        return contrib;
      }
      return {
        id: this.playground.id,
        contributions: {
          hasContriPic: false,
          hasContriPlay: {
            description: false,
            facilites: false,
            location: false,
            street: false,
          },
          hasContriSche: false,
        },
      };
    },
    getContributions() {
      const contrib = this.listContributions.find((play) => play.id === this.playground.id);
      if (contrib) {
        return contrib;
      }
      // Return null if the API has not been called
      return null;
    },
    getContributors() {
      const playground = this.listContributors.find((play) => play.id === this.playground.id);
      return playground ? playground.contributors : [];
    },
    // TOOLBAR -----------------------------------------------------------------------------------------
    displayToolbar() {
      return this.active && this.mobileLayout && this.detail;
    },
    toolbarColor() {
      return this.$vuetify.theme.dark ? 'semi' : 'primary';
    },

    // CONTRIBUTION ------------------------------------------------------------------------------------
    unSaveContribution() {
      return this.ongoingContrib > 0;
    },

    // REPORT FORM -------------------------------------------------------------------------------------
    reportMessageIsSet() {
      // The message is not null and is not empty
      return this.reportMessage?.length > 0;
    },
    reportMessageIsValid() {
      // The message is set and contains between 10 and 400 characters
      return this.reportMessageIsSet && this.reportMessage.length >= 10 && this.reportMessage.length <= 400;
    },

    // PUB ---------------------------------------------------------------------------------------------
    canDisplayAd() {
      return !!this.detail && this.active;
    },
    // NETWORK SHARING
    placeNameHashtag() {
      return this.playgroundPlaceName.normalize('NFD').replace(/([\u0300-\u036f]|[^\w])/g, '').toLowerCase();
    },
  },
  watch: {
    // TOOLBAR -----------------------------------------------------------------------------------------
    displayToolbar() {
      this.disconnectAppBarObserver();
    },

    // CONTENT -----------------------------------------------------------------------------------------
    active(value) {
      if (!value) {
        this.closeAllForm();
        if (this.contributionBanner) {
          // Close the banner in mobile
          this.resetContributionBanner();
        }
      }
    },
    playground: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue?.id !== oldValue?.id) {
          if (Object.keys(this.errors$).length) {
            this.errors$.reset();
          }
          this.initPlayground();
          this.$refs.scrollContainer?.scrollToPosition(0);
          this.loadingFavorite = false;
          // Close the banner in desktop
          this.resetContributionBanner();
        }
      },
    },
    getListFavorites(value) {
      if (this.detail) {
        this.detail.is_favorite = value.includes(this.detail.id);
      }
    },

    // CONTRIBUTION ------------------------------------------------------------------------------------
    unSaveContribution: {
      immediate: true,
      handler(value) {
        this.$emit('unsave-contribution', value);
      },
    },

    // REPORT FORM -------------------------------------------------------------------------------------
    reportMessage() {
      // When the message changes, if it is not set or if it is valid, set the error to false
      if (!this.reportMessageIsSet || this.reportMessageIsValid) {
        this.errors$.handleError(false, 'reports', 'message');
      }
    },
    reportReason() {
      this.errors$.handleError(false, 'reports', 'reason');
    },

    sharedError(value) {
      this.errors$.mergeErrors(value);
    },
  },
  mounted() {
    this.errors$.mergeErrors(this.sharedError);
  },
  updated() {
    this.observeAppBarObserver();
  },
  unmounted() {
    this.disconnectAppBarObserver();
  },
  methods: {
    ...mapActions({
      addToFavorites: 'transferData/addToFavorites',
      toggleSide: 'menu/toggleSide',
      initSnackbar: 'snackbar/initSnackbar',
      removeFromFavorites: 'transferData/removeFromFavorites',
      updateKeepAlive: 'transferData/updateKeepAlive',
      updateListAverageGrade: 'transferData/updateListAverageGrade',
    }),

    // DOM UPDATE LISTENER -----------------------------------------------------------------------------
    observeAppBarObserver() {
      if (this.$refs.appBar && !this.appBarObserver) {
        this.appBarObserver = new MutationObserver((mutations) => {
          mutations.forEach((m) => {
            const newValue = m.target.getAttribute(m.attributeName);
            this.$nextTick(() => {
              this.appBarHidden = newValue.includes('v-app-bar--hide-shadow');
            });
          });
        });

        this.appBarObserver.observe(this.$refs.appBar.$el, {
          attributes: true,
          attributeOldValue: true,
          attributeFilter: ['class'],
        });
      }

      this.appBarHidden = this.appBarHidden && !!this.$refs.appBar;
    },
    disconnectAppBarObserver() {
      if (this.appBarObserver) {
        this.appBarObserver.disconnect();
        this.appBarObserver = null;
      }
    },
    onScroll(scrollTop) {
      if (this.displayToolbar) {
        const carouselHeight = this.$refs.carousel.$el.offsetHeight;
        this.hoverCarousel = scrollTop < carouselHeight;
      }
    },

    // LOAD CONTENT ------------------------------------------------------------------------------------
    initPlayground() {
      if (!this.playground) {
        this.detail = null;
        return;
      }

      const shapeId = getShapeId(this.playground.city, this.playground.sub);

      this.detail = {
        id: this.playground.id,
        admin: {
          alias: this.playground.admin ? this.playground.admin.alias : this.playground.country.alias,
        },
        place: {
          shapeId,
          shapeIsSub: shapeId !== this.playground.city.id,
          alias: this.playgroundPlaceAlias,
          name: this.playgroundPlaceName,
        },
        location: {
          lat: this.playground.location.lat,
          lon: this.playground.location.lon,
        },
        schedules: this.playground.schedules,
        street: this.playground.street,
        description: this.playground.description || '',
        number_grade: this.playground.number_grade,
        average_grade: this.playground.average_grade,
        pictures: this.playground.pictures.map((uuid) => ({ uuid, alt: `Image de l'aire de jeux ${this.placeNameWithPreposition}, ${this.playground.street}` })),
        facilities: [...this.facilitiesData].map((facility) => ({ ...facility, value: this.playground.facilities[facility.id] })),
        is_favorite: this.getListFavorites.includes(this.playground.id),
      };
    },

    // LOGIN -------------------------------------------------------------------------------------------
    openLogin(origin) {
      if (origin === 'carousel-report') {
        this.openCarouselReport = false;
        this.waitCarouselReport = true;
      } else if (origin === 'comment-report') {
        this.openCommentReport = false;
        this.waitCommentReport = true;
      } else if (origin === 'contributors-report') {
        this.openContributorsReport = false;
        this.waitContributorsReport = true;
      }
      this.modalLogin = true;
      this.fullscreenTitle = 'Connexion';
      this.fullscreenLabel = 'Fenêtre de connexion';
      this.fullscreenOptions = {
        scrollable: true,
        width: '600',
      };
      this.showFullscreen = true;
    },
    async onLogin() {
      await this.resetFullscreens();
      this.modalLogin = false;
      if (this.waitFavCall) {
        this.setFavoriteApi(this.detail);
      } else if (this.modalReport) {
        this.openReport();
      } else if (this.waitCarouselReport) {
        this.openCarouselReport = true;
        this.waitCarouselReport = false;
      } else if (this.waitCommentReport) {
        this.openCommentReport = true;
        this.waitCommentReport = false;
      } else if (this.waitContributorsReport) {
        this.openContributorsReport = true;
        this.waitContributorsReport = false;
      }
    },

    // CONTRIBUTION ------------------------------------------------------------------------------------
    closeAllForm() {
      this.$refs.carousel?.closeCarouselForm();
      this.$refs.street?.closeStreetForm();
      this.$refs.facilities?.closeFacilitiesForm();
      this.$refs.description?.closeDescriptionForm();
      this.$refs.map?.closeMapForm();
      this.$refs.commentForm?.closeCommentForm();
      this.$refs.detailComment?.forEach((compo) => {
        compo.closeComment();
      });
    },
    updateUnsave(value) {
      this.ongoingContrib += value ? 1 : -1;
    },
    visibilityChanged(isVisible, entry, ref) {
      this.formVisible[ref] = isVisible;
    },
    updateListContributions(type, contribution) {
      this.showContributionBanner(type);
      const listHasContributions = this.getHasContributions;
      let listContributions = this.getContributions;
      if (type === 'schedule') {
        // For the schedules, we just need to know that there is a contribution
        listHasContributions.contributions.hasContriSche = true;
        this.$emit('update-detail-data', { listContributions: null, listHasContributions });
      } else {
        // If not yet defined, set the default values
        if (!listContributions) {
          listContributions = {
            id: this.playground.id,
            street: null,
            description: null,
            facilities: null,
            location: {
              lat: null,
              lon: null,
            },
          };
        }
        const listKeys = Object.keys(listContributions);
        if (listKeys.includes(type)) {
          listContributions[type] = contribution;
          listHasContributions.contributions.hasContriPlay[type] = true;
          this.$emit('update-detail-data', { listContributions, listHasContributions });
        }
      }
    },
    deleteContribution(type) {
      const listHasContributions = this.getHasContributions;
      const listContributions = this.getContributions;
      if (type === 'schedule') {
        // For the schedules, we just need to know that there is a contribution
        listHasContributions.contributions.hasContriSche = false;
      } else {
        const listKeys = Object.keys(listContributions);
        if (listKeys.includes(type)) {
          listContributions[type] = null;
          listHasContributions.contributions.hasContriPlay[type] = false;
          this.$emit('update-detail-data', { listContributions, listHasContributions });
        }
      }
    },

    // FAVORITE ----------------------------------------------------------------------------------------
    changeFavorite(detail) {
      this.waitFavCall = true;
      this.loadingFavorite = true;
      if (!this.isAuth) {
        this.openLogin();
        return;
      }
      clearTimeout(this.favoriteTimer);
      this.favoriteTimer = setTimeout(() => {
        this.setFavoriteApi(detail);
      }, 1000);
    },
    setFavoriteApi(detail) {
      if (this.getContext === 'favorite') {
        this.updateKeepAlive(false);
      }
      const removeFav = detail.is_favorite;
      const action = removeFav ? 'retirée des' : 'ajoutée aux';
      const apiFav = removeFav
        ? apiFavorite.deleteFavorite(detail.id, this)
        : apiFavorite.addFavorite({ playground: detail.id }, this);

      apiFav.then(() => {
        if (this.detail.id === detail.id) {
          detail.is_favorite = !removeFav;
          this.detail = { ...detail };
        }
        if (removeFav) {
          this.removeFromFavorites(detail.id);
        } else {
          this.addToFavorites(detail.id);
        }
        this.displayInfoSnackbar(`Aire de jeux ${action} favoris`, '');
      }).catch((error) => {
        // Validation error
        if (error.response && (error.response.status === 400 || error.response.status === 404)) {
          let favErrorMsg = `L'aire de jeux n'a pas pu être ${action} favoris`;
          if (this.errors$.get('new_favorite', 'playground')) {
            favErrorMsg = this.errors$.get('new_favorite', 'playground');
          } else if (this.errors$.get('new_favorite', 'form')) {
            favErrorMsg = this.errors$.get('new_favorite', 'form');
          }
          this.displayInfoSnackbar(favErrorMsg, 'error');
        }
      }).finally(() => {
        this.waitFavCall = false;
        this.loadingFavorite = false;
      });
    },

    // COMMENT -----------------------------------------------------------------------------------------
    addComment(comment) {
      const listComments = this.getComments;
      const listHasComments = this.getHasComment;

      const newComment = {
        created_at: comment.created_at,
        grade: comment.grade,
        id: comment.id,
        text: comment.text,
        user: {
          alias: this.getUser.alias,
          archive: false,
          avatar: this.getUser.avatar,
          name: this.getUser.name,
          lvl: this.getUser.lvl,
        },
      };

      if (listComments.results.unshift(newComment) > 5) {
        listComments.results.pop();
      }
      this.detail.number_grade += 1;
      this.detail.average_grade = comment.average_grade;
      listHasComments.hasComment = true;

      this.showContributionBanner('comment');
      // update average grade and number grade in Search Result to sync with List
      this.updateListAverageGrade({ playId: this.detail.id, averageGrade: comment.average_grade, numberGrade: this.detail.number_grade });
      this.$emit('update-detail-data', { listComments, listHasComments });
    },
    editComment(comment) {
      if (this.getContext === 'comment') {
        this.updateKeepAlive(false);
      }
      const listComments = this.getComments;
      const indexComment = listComments.results.findIndex(
        (element) => element.id === comment.id,
      );
      listComments.results[indexComment].grade = comment.grade;
      listComments.results[indexComment].text = comment.text;
      this.detail.average_grade = comment.average_grade;
      // update average grade in Search Result to sync with List
      this.updateListAverageGrade({ playId: this.detail.id, averageGrade: comment.average_grade });
      this.$emit('update-detail-data', { listComments });
    },
    deleteComment(comment) {
      if (this.getContext === 'comment') {
        this.updateKeepAlive(false);
      }
      const listComments = this.getComments;
      const listHasComments = this.getHasComment;
      listComments.results = listComments.results.filter(
        (element) => element.id !== comment.id,
      );
      listHasComments.hasComment = false;
      this.detail.average_grade = comment.average_grade;
      this.detail.number_grade -= 1;
      // update average grade and number grade in Search Result to sync with List
      this.updateListAverageGrade({ playId: this.detail.id, averageGrade: comment.average_grade, numberGrade: this.detail.number_grade });
      this.$emit('update-detail-data', { listComments, listHasComments });
    },

    // REPORT ------------------------------------------------------------------------------------------
    openReport() {
      this.listReasons = reasons.playground.value.filter((reason) => reason.id !== 4);
      this.modalReport = true;

      if (!this.isAuth) {
        this.openLogin();
        return;
      }
      this.fullscreenTitle = 'Signalement';
      this.fullscreenLabel = 'Fenêtre de signalement';
      this.fullscreenOptions = {
        scrollable: true,
        width: '500',
      };
      this.fullscreenButtons = {
        closeText: 'Annuler',
        confirmText: 'Valider',
      };
      this.showFullscreen = true;
    },
    sendReport() {
      this.reportMessage = removeWhitespaces(this.reportMessage);

      // prevalidation
      this.errors$.reset();
      this.errorReportToDisplay = null;
      this.errors$.handleError(!this.reportReason, 'reports', 'reason');
      this.errors$.handleError(this.reportMessageIsSet && !this.reportMessageIsValid, 'reports', 'message');

      this.errors$.handleError(
        this.reportReason === 3 && !this.reportUuid && (!this.reportMessage || this.reportMessage.length === 0),
        'reports',
        'form',
        'message_or_picture_required',
      );
      if (this.errors$.hasErrors()) {
        this.errors$.focusFirstError();
        return;
      }

      const data = {
        context: 'playground',
        playground: this.detail.id,
        reason: this.reportReason,
        message: this.reportMessage,
        report_uuid: this.reportUuid,
      };

      this.loadingReport = true;
      apiReport.report(data, this)
        .then(() => {
          this.resetReport();
          this.displayInfoSnackbar('Votre signalement a été pris en compte.', 'success');
        })
        .catch((error) => {
          // Validation error
          if (error.response && error.response.status === 400) {
            if (this.errors$.hasErrors()) {
              this.errors$.focusFirstError();
            }
          }
        }).finally(() => {
          this.loadingReport = false;
        });
    },
    resetReport() {
      this.resetFullscreens();
      this.errors$.reset();
      this.reportReason = null;
      this.reportUuid = null;
      this.reportMessage = null;
      this.modalReport = false;
      this.listReasons = [];
    },

    // BANNER + DIALOG + SNACKBAR ----------------------------------------------------------------------
    showContributionBanner(type) {
      setTimeout(() => {
        this.contributionBannerText = 'Merci pour votre contribution, elle sera validée après modération';
        if (type === 'comment') {
          this.contributionBannerText = 'Merci pour votre avis';
        }
        this.contributionBanner = true;
      }, 500);
    },
    resetContributionBanner() {
      this.contributionBanner = false;
      this.contributionBannerText = null;
    },
    bannerFavorite(detail) {
      this.resetContributionBanner();
      this.changeFavorite(detail);
    },
    resetFullscreens() {
      this.showFullscreen = false;
      this.fullscreenOptions = {};
      this.fullscreenTitle = '';
      this.fullscreenLabel = '';
      this.fullscreenButtons = {};
    },
    closeFullscreen() {
      if (this.modalLogin) {
        this.waitFavCall = false;
        this.waitCarouselReport = false;
        this.waitCommentReport = false;
        this.waitContributorsReport = false;
        this.modalLogin = false;
        this.loadingFavorite = false;
        this.closeAllForm();
      }
      this.resetReport();
    },
    displayInfoSnackbar(content, color) {
      this.initSnackbar({
        options: {
          color,
        },
        content,
      });
    },
    openSharing(network) {
      if (this.$matomo) {
        this.$matomo.trackEvent('Sharing', 'openSharing', network);
      }
    },

    // NAVIGATION --------------------------------------------------------------------------------------
    goToComments() {
      this.$refs.comments.$el.scrollIntoView({ behavior: 'smooth' });
    },
    // -------------------------------------------------------------------------------------------------

  },
};
